import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderCog,
  FolderOpen,
  Lock,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { type FormEvent, Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { BulkActionBar, RowCheckbox } from '../components/data-table'
import { EntityAccessPanel } from '../components/entity-access-panel'
import { ManualGroupPanel } from '../components/manual-group-panel'
import { ProjectHub } from '../components/project-hub/project-hub'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { api, type Project } from '../lib/api'
import { CreationAttempt } from '../lib/creation-attempt'
import { useDataTable } from '../lib/data-table'
import { type GroupOption, useGroupedTable } from '../lib/grouped-table'
import { notify } from '../lib/notify'
import { useIsMobile } from '../lib/use-media-query'
import { Route as rootRoute } from './__root'

const PROJECT_GROUP_OPTIONS: GroupOption[] = [
  { key: 'none', label: 'Keine' },
  { key: 'customer', label: 'Kunde' },
  { key: 'status', label: 'Status' },
]

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects',
  validateSearch: (s: Record<string, unknown>): { id?: string } => ({
    id: typeof s.id === 'string' ? s.id : undefined,
  }),
  component: ProjectsPage,
})

export function ProjectsPage() {
  const { id: activeId } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [dialog, setDialog] = useState<{ open: boolean; project: Project | null; session: number }>(
    {
      open: false,
      project: null,
      session: 0,
    },
  )
  const opening = useRef(0)
  const openDialog = async (project: Project | null) => {
    const generation = ++opening.current
    try {
      const fresh = project
        ? (
            await queryClient.fetchQuery({
              queryKey: ['project-edit', project.id, generation],
              queryFn: () => api.getProject(project.id),
              staleTime: 0,
              gcTime: 0,
            })
          ).project
        : null
      if (generation === opening.current)
        setDialog((current) => ({ open: true, project: fresh, session: current.session + 1 }))
    } catch (error) {
      if (generation === opening.current) notify.error((error as Error).message)
    }
  }
  const closeDialog = (session: number) =>
    setDialog((current) =>
      current.session === session ? { ...current, open: false, project: null } : current,
    )
  // Mobile-Shell (<640): Suche-Sheet hinterm Bottom-Slot, Titel oben.
  const [searchSheet, setSearchSheet] = useState(false)
  useMobilePageTitle('Projekte')
  const [accessPanel, setAccessPanel] = useState<{
    open: boolean
    project: Project | null
    bulkIds: string[] | null
  }>({ open: false, project: null, bulkIds: null })
  const [groupPanel, setGroupPanel] = useState<
    { open: boolean; mode: 'assign'; ids: string[] } | { open: boolean; mode: 'manage' }
  >({ open: false, mode: 'manage' })
  const [showArchived, setShowArchived] = useState(false)
  // Admin-Mehrfachauswahl: Checkboxen nur im expliziten Auswahl-Modus —
  // die kompakte Liste bleibt sonst frei von Tabellen-Chrome.
  const [selectMode, setSelectMode] = useState(false)

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data: customersData } = useQuery({
    queryKey: ['customers', { archived: false }],
    queryFn: () => api.listCustomers(false),
    enabled: !!meData?.user,
  })
  const { data: projectsData, isLoading } = useQuery({
    queryKey: ['projects', { archived: showArchived }],
    queryFn: () => api.listProjects({ archived: showArchived }),
    enabled: !!meData?.user,
  })
  const { data: groupsData } = useQuery({
    queryKey: ['groups', 'project'],
    queryFn: () => api.listEntityGroups('project'),
    enabled: !!meData?.user,
  })
  const projectGroups = groupsData?.groups ?? []
  const isAdminUser = meData?.user?.role === 'admin'

  const customers = customersData?.customers ?? []
  const customerMap = useMemo(() => new Map(customers.map((c) => [c.id, c.name])), [customers])
  const projects = projectsData?.projects ?? []

  const enriched = useMemo(
    () =>
      projects.map((p) => ({
        ...p,
        _customerName: customerMap.get(p.customerId) ?? '—',
      })),
    [projects, customerMap],
  )

  // Kompakte Seitenliste: Suche + Auswahl aus useDataTable, aber ohne
  // Pagination-UI — pageSize 500 zeigt praktisch immer alles, eigener
  // storageKey damit das alte Tabellen-Setting (25) nicht hineinlappt.
  const table = useDataTable<(typeof enriched)[number]>(
    enriched,
    { name: 'name' },
    {
      searchableFields: ['name', '_customerName'],
      defaultSortKey: 'name',
      defaultSortDir: 'asc',
      defaultPageSize: 500,
      storageKey: 'projects-hub',
    },
  )

  const grouped = useGroupedTable<(typeof enriched)[number]>(table, {
    pageKey: 'projects',
    options: PROJECT_GROUP_OPTIONS,
    manualGroups: projectGroups,
    manualGroupsEnabled: isAdminUser,
    getId: (p) => p.id,
    groupBy: (p, mode) => {
      if (mode === 'customer') {
        const label = p._customerName || 'Ohne Kunde'
        return { key: p.customerId || '__none__', label }
      }
      if (mode === 'status') {
        return p.archived
          ? { key: 'archived', label: 'Archiviert' }
          : { key: 'active', label: 'Aktiv' }
      }
      return null
    },
  })

  const queryClient = useQueryClient()
  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteProjects(ids),
    onSuccess: (r, ids) => {
      table.clearSelection()
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      if (activeId && ids.includes(activeId)) {
        navigate({ search: { id: undefined } })
      }
      notify.success(`${r.deleted} Projekte gelöscht`)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Phone: Master-Detail-Weiche über den ?id-Search-Param (JS-Gate statt
  // CSS — Muster chat.tsx, hält das 640–767-Band konsistent).
  const isPhone = useIsMobile()

  // Auswahl-Modus verlassen → hängige Auswahl räumen.
  const toggleSelectMode = () => {
    if (selectMode) table.clearSelection()
    setSelectMode((v) => !v)
  }

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  const isAdmin = meData.user.role === 'admin'

  const openProject = (id: string) => navigate({ search: { id } })
  const closeProject = () => navigate({ search: { id: undefined } })
  const activeCustomerName = activeId
    ? enriched.find((p) => p.id === activeId)?._customerName
    : undefined

  const listVisible = !isPhone || !activeId
  const hubVisible = !isPhone || !!activeId

  const renderRow = (p: (typeof enriched)[number]) => (
    <li key={p.id}>
      <div
        className={`group flex items-center gap-2 pr-2 transition-colors ${
          p.id === activeId ? 'bg-accent/10' : 'hover:bg-bg-overlay/40'
        } ${p.archived ? 'opacity-60' : ''}`}
      >
        {isAdmin && selectMode && (
          <span className="pl-2.5">
            <RowCheckbox
              checked={table.isSelected(p.id)}
              onToggle={() => table.toggleRow(p.id)}
              label={`${p.name} auswählen`}
            />
          </span>
        )}
        <button
          type="button"
          onClick={() => (isAdmin && selectMode ? table.toggleRow(p.id) : openProject(p.id))}
          className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 text-left"
        >
          <span
            aria-hidden
            className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
            style={{ backgroundColor: p.color ?? 'var(--color-border-default, #6b6259)' }}
          />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              {p.restricted && (
                <Lock className="h-3 w-3 text-accent shrink-0" aria-label="Beschränkter Zugriff" />
              )}
              <span className="truncate text-sm text-text-primary" title={p.name}>
                {p.name}
              </span>
            </span>
            <span className="block truncate text-[11px] text-text-tertiary" title={p._customerName}>
              {p._customerName}
            </span>
          </span>
        </button>
      </div>
    </li>
  )

  return (
    <section className="h-full min-h-0 sm:h-[calc(100dvh-9.5rem-var(--app-safe-bottom))] lg:h-[calc(100dvh-6rem)] phone:h-full flex flex-col animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Suche + ＋ nur solange
          kein Projekt offen ist — bei offenem Detail sitzt ← oben. */}
      {!(isPhone && activeId) && (
        <MobileBottomSlots>
          <BarSlot
            label="Suche"
            icon={<Search className="h-5 w-5" />}
            active={table.search !== ''}
            ariaLabel="Projekte durchsuchen"
            ariaHasPopup
            onClick={() => setSearchSheet(true)}
          />
          <BarAccentPlus
            ariaLabel="Neues Projekt"
            onClick={() => openDialog(null)}
            disabled={customers.length === 0}
            title={customers.length === 0 ? 'Lege zuerst einen Kunden an' : undefined}
          />
        </MobileBottomSlots>
      )}

      {/* Suche-Sheet (<640): bindet dieselbe table.search wie die Listen-Suche. */}
      <SidePanel open={searchSheet} onClose={() => setSearchSheet(false)} title="Suche">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
          <Input
            value={table.search}
            onChange={(e) => table.setSearch(e.target.value)}
            placeholder="Projekte durchsuchen …"
            className="pl-8"
            autoFocus
          />
        </div>
      </SidePanel>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden; PageHeader = einheitlicher Desktop-Kopf
          aus dem Header-Umbau (PR #76/#81). */}
      <PageHeader
        title="Projekte"
        actions={
          <Button
            onClick={() => openDialog(null)}
            size="lg"
            disabled={customers.length === 0}
            title={customers.length === 0 ? 'Lege zuerst einen Kunden an' : undefined}
          >
            <Plus className="h-4 w-4" /> Neues Projekt
          </Button>
        }
      />

      {/* isPhone deckt auch das Handy-Querformat ≥768px ab (pointer:coarse +
          max-height 500) — dort rendert nur EIN Pane, das sonst per
          Auto-Placement in der schmalen 280px-Spalte landen würde. */}
      <div
        className={
          isPhone
            ? 'flex-1 grid grid-cols-1 gap-4 min-h-0 pt-4'
            : 'flex-1 grid grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)] gap-4 min-h-0 pt-4'
        }
      >
        {/* Kompakte Projektliste (Master) */}
        {listVisible && (
          <aside className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden flex flex-col min-h-0">
            <header className="px-3 py-2.5 border-b border-border-subtle bg-bg-overlay/30 space-y-2">
              <div className="flex items-center justify-between gap-1">
                <span className="text-xs uppercase tracking-wider text-text-tertiary">
                  Projekte ({table.filteredRows})
                </span>
                <div className="flex items-center gap-0.5">
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={toggleSelectMode}
                      title={selectMode ? 'Auswahl-Modus beenden' : 'Mehrere auswählen'}
                      aria-pressed={selectMode}
                      className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                        selectMode
                          ? 'text-accent bg-accent/10'
                          : 'text-text-tertiary hover:text-accent hover:bg-bg-overlay/40'
                      }`}
                    >
                      {selectMode ? (
                        <X className="h-3.5 w-3.5" />
                      ) : (
                        <CheckSquare className="h-3.5 w-3.5" />
                      )}
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => setGroupPanel({ open: true, mode: 'manage' })}
                      title="Gruppen verwalten"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-tertiary hover:text-accent hover:bg-bg-overlay/40 transition-colors"
                    >
                      <FolderCog className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openDialog(null)}
                    disabled={customers.length === 0}
                    title={customers.length === 0 ? 'Lege zuerst einen Kunden an' : 'Neues Projekt'}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-tertiary hover:text-accent hover:bg-bg-overlay/40 transition-colors disabled:opacity-40"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
                <Input
                  value={table.search}
                  onChange={(e) => table.setSearch(e.target.value)}
                  placeholder="Suchen …"
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <label className="flex items-center gap-1 text-text-tertiary">
                  Gruppieren
                  <select
                    value={grouped.groupBy}
                    onChange={(e) => grouped.setGroupBy(e.target.value)}
                    className="bg-transparent text-text-secondary text-[11px] focus:outline-none cursor-pointer"
                  >
                    {grouped.options.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => setShowArchived((v) => !v)}
                  className="text-text-tertiary hover:text-accent transition-colors whitespace-nowrap"
                >
                  {showArchived ? 'Aktive zeigen' : 'Archivierte zeigen'}
                </button>
              </div>
            </header>

            {isLoading ? (
              <p className="px-4 py-8 text-center text-sm text-text-tertiary">Lade …</p>
            ) : projects.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-text-tertiary space-y-3">
                <FolderOpen className="h-10 w-10 mx-auto text-text-tertiary/60" strokeWidth={1.5} />
                <p>
                  {customers.length === 0
                    ? 'Lege erst einen Kunden an, dann ein Projekt.'
                    : 'Noch keine Projekte. Leg eines an.'}
                </p>
                {customers.length > 0 && (
                  <Button size="sm" onClick={() => openDialog(null)}>
                    <Plus className="h-4 w-4" /> Neues Projekt
                  </Button>
                )}
              </div>
            ) : table.rows.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-text-tertiary">Keine Treffer.</p>
            ) : (
              <ul className="flex-1 overflow-y-auto divide-y divide-border-subtle">
                {grouped.disabled
                  ? table.rows.map(renderRow)
                  : grouped.groups.map((g) => (
                      <Fragment key={g.key}>
                        <li>
                          <button
                            type="button"
                            onClick={() => grouped.toggleGroup(g.key)}
                            className="w-full flex items-center gap-1.5 px-3 py-1.5 bg-bg-overlay/30 text-left"
                          >
                            {g.collapsed ? (
                              <ChevronRight className="h-3 w-3 text-text-tertiary shrink-0" />
                            ) : (
                              <ChevronDown className="h-3 w-3 text-text-tertiary shrink-0" />
                            )}
                            <span
                              className={`truncate text-[11px] uppercase tracking-wider ${
                                g.muted ? 'text-text-tertiary/70' : 'text-text-tertiary'
                              }`}
                            >
                              {g.label}
                            </span>
                            <span className="text-[11px] text-text-tertiary tabular-nums shrink-0">
                              {g.items.length}
                            </span>
                          </button>
                        </li>
                        {!g.collapsed && g.items.map(renderRow)}
                      </Fragment>
                    ))}
              </ul>
            )}
            {/* Kein stiller Cap: die Liste zeigt max. eine Seite (500) —
                sag es, statt Projekte kommentarlos zu verschlucken. */}
            {table.filteredRows > table.rows.length && (
              <p className="px-3 py-2 text-[11px] text-text-tertiary border-t border-border-subtle shrink-0">
                {table.filteredRows - table.rows.length} weitere Projekte nicht gelistet — Suche
                verfeinern.
              </p>
            )}
          </aside>
        )}

        {/* Projektinhalt (Detail) */}
        {hubVisible && (
          <main className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden flex flex-col min-h-0">
            {activeId ? (
              <ProjectHub
                projectId={activeId}
                myId={meData.user.id}
                isAdmin={isAdmin}
                isPhone={isPhone}
                customerName={activeCustomerName}
                onBack={closeProject}
                onEdit={(p) => openDialog(p)}
                onManageAccess={(p) => setAccessPanel({ open: true, project: p, bulkIds: null })}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-text-tertiary">
                <FolderOpen className="h-12 w-12 text-text-tertiary/60 mb-3" strokeWidth={1.5} />
                <p className="text-sm">Wähle links ein Projekt aus oder leg ein neues an.</p>
              </div>
            )}
          </main>
        )}
      </div>

      {isAdmin && selectMode && (
        <BulkActionBar
          selectionCount={table.selectionCount}
          filteredRows={table.filteredRows}
          onClear={table.clearSelection}
          onSelectAllFiltered={table.selectAllFiltered}
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setGroupPanel({
                open: true,
                mode: 'assign',
                ids: Array.from(table.selectedIds),
              })
            }
          >
            <Folder className="h-3.5 w-3.5" /> Zur Gruppe
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setAccessPanel({
                open: true,
                project: null,
                bulkIds: Array.from(table.selectedIds),
              })
            }
          >
            <Lock className="h-3.5 w-3.5" /> Freigabe
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={bulkDelete.isPending}
            onClick={async () => {
              const ids = Array.from(table.selectedIds)
              const ok = await confirm({
                title: `${ids.length} Projekte löschen?`,
                description:
                  'Time-Entries verlieren ihren Project-Bezug. Diese Aktion ist unumkehrbar.',
                variant: 'danger',
                confirmLabel: 'Endgültig löschen',
              })
              if (ok) bulkDelete.mutate(ids)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Löschen
          </Button>
        </BulkActionBar>
      )}

      <ProjectDialog
        key={dialog.session}
        open={dialog.open}
        project={dialog.project}
        customers={customers}
        isAdmin={meData?.user.role === 'admin'}
        onClose={() => closeDialog(dialog.session)}
      />
      <EntityAccessPanel
        open={accessPanel.open}
        onClose={() => setAccessPanel({ open: false, project: null, bulkIds: null })}
        resourceType="project"
        resourceId={accessPanel.project?.id ?? null}
        resourceIds={accessPanel.bulkIds ?? undefined}
        resourceName={
          accessPanel.bulkIds
            ? `${accessPanel.bulkIds.length} Projekte`
            : (accessPanel.project?.name ?? '')
        }
      />
      {isAdmin &&
        (groupPanel.mode === 'assign' ? (
          <ManualGroupPanel
            open={groupPanel.open}
            onClose={() => setGroupPanel({ open: false, mode: 'manage' })}
            resourceType="project"
            mode="assign"
            resourceIds={groupPanel.ids}
          />
        ) : (
          <ManualGroupPanel
            open={groupPanel.open}
            onClose={() => setGroupPanel({ open: false, mode: 'manage' })}
            resourceType="project"
            mode="manage"
          />
        ))}
    </section>
  )
}

const NAME_MAX = 120
const DESC_MAX = 4000

function ProjectDialog({
  open,
  project,
  customers,
  isAdmin,
  onClose,
}: {
  open: boolean
  project: Project | null
  customers: { id: string; name: string }[]
  isAdmin: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const active = useRef(true)
  const isEdit = !!project
  const [customerId, setCustomerId] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [hourlyRate, setHourlyRate] = useState('')
  const [budget, setBudget] = useState('')
  const [color, setColor] = useState<string>('')
  const [linkedDocIds, setLinkedDocIds] = useState<Set<string>>(new Set())
  const documentsTouched = useRef(false)
  const [creationAttempt] = useState(
    () => new CreationAttempt<Parameters<typeof api.createProject>[0]>(),
  )
  // #1007: abweichende emberfibu-Firma je Projekt (nur bei Schalter an/exklusiv
  // und für Admins sichtbar; sonst gilt die für den Kunden gemerkte Firma).
  // undefined = no user edit; empty string = explicit removal, also after a lost PUT.
  const [fibuFirmaId, setFibuFirmaId] = useState<string>()
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  const close = () => {
    active.current = false
    onClose()
  }
  const { data: fibuStatus } = useQuery({
    queryKey: ['fibu-status'],
    queryFn: () => api.fibuStatus(),
    enabled: open,
  })
  const fibuFirmen = fibuStatus && fibuStatus.modus !== 'aus' ? fibuStatus.firmen : []
  const { data: fibuProjekt, isError: fibuLadefehler } = useQuery({
    queryKey: ['fibu-projekt-firma', project?.id],
    queryFn: () => api.fibuProjektFirma(project?.id ?? ''),
    enabled: open && !!project && fibuFirmen.length > 0,
  })

  // All documents the user can see — used as the picker's pool. Cheap query
  // (already cached for /documents page) so we just reuse it.
  const { data: allDocs } = useQuery({
    queryKey: ['documents', 'outgoing'],
    queryFn: () => api.listDocuments({}),
    enabled: open,
  })
  // Documents currently linked to THIS project. Only fetch when editing.
  const { data: projDocs } = useQuery({
    queryKey: ['project-documents', project?.id, project?.version],
    queryFn: () => (project ? api.listProjectDocuments(project.id) : Promise.resolve(null)),
    enabled: open && !!project,
  })

  useEffect(() => {
    if (!open) return
    setFibuFirmaId(undefined)
    documentsTouched.current = false
    creationAttempt.reset()
    if (project) {
      setCustomerId(project.customerId)
      setName(project.name)
      setDescription(project.description ?? '')
      // API returns ISO timestamps, <input type=date> wants YYYY-MM-DD.
      setStartDate(project.startDate ? project.startDate.slice(0, 10) : '')
      setEndDate(project.endDate ? project.endDate.slice(0, 10) : '')
      setHourlyRate(project.hourlyRateCents != null ? String(project.hourlyRateCents / 100) : '')
      setBudget(project.budgetHours ?? '')
      setColor(project.color ?? '')
    } else {
      setCustomerId('')
      setName('')
      setDescription('')
      setStartDate('')
      setEndDate('')
      setHourlyRate('')
      setBudget('')
      setColor('')
      setLinkedDocIds(new Set())
    }
  }, [open, project, creationAttempt])

  // Once project documents arrive, seed the selection set
  useEffect(() => {
    if (projDocs?.documents && !documentsTouched.current) {
      setLinkedDocIds(new Set(projDocs.documents.map((d) => d.id)))
    }
  }, [projDocs])

  const save = useMutation({
    mutationFn: async () => {
      const cents = hourlyRate ? Math.round(Number(hourlyRate) * 100) : null
      const budgetH = budget ? Number(budget) : null
      const payload = {
        customerId,
        name: name.trim(),
        description: description.trim() || null,
        startDate: startDate || null,
        endDate: endDate || null,
        hourlyRateCents: cents,
        budgetHours: budgetH,
        color: color.trim() || null,
      }
      const complete = {
        ...payload,
        ...(isAdmin && fibuFirmen.length > 0 && fibuFirmaId !== undefined
          ? { firmaId: fibuFirmaId || null }
          : {}),
        ...(isAdmin && (!project || documentsTouched.current)
          ? { documentIds: Array.from(linkedDocIds) }
          : {}),
        ...(project ? { baseVersion: project.version } : {}),
      }
      return creationAttempt.run(complete, (data, key, revision) =>
        project
          ? api.updateProject(project.id, data, key, revision)
          : api.createProject(data, key, revision),
      )
    },
    // A closed form still owns its submitted save. Refresh shared data even
    // after an error or a committed response lost in transit.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ queryKey: ['fibu-projekt-firma'] }),
        queryClient.invalidateQueries({ queryKey: ['fibu-ziel'] }),
        queryClient.invalidateQueries({ queryKey: ['project'] }),
        queryClient.invalidateQueries({ queryKey: ['project-documents'] }),
        queryClient.invalidateQueries({ queryKey: ['documents'] }),
      ]),
    onSuccess: () => {
      if (!active.current) return
      notify.success(isEdit ? 'Projekt aktualisiert' : 'Projekt angelegt')
      close()
    },
    onError: (err: Error) => {
      if (active.current) notify.error(err.message)
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!customerId || !name.trim() || save.isPending) return
    save.mutate()
  }

  function toggleDoc(id: string) {
    documentsTouched.current = true
    setLinkedDocIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // End-before-start sanity check (UI-only — backend allows either order).
  const dateRangeInvalid =
    !!startDate && !!endDate && new Date(endDate).getTime() < new Date(startDate).getTime()

  const documents = (allDocs?.documents ?? []).filter((d) => !d.archivedAt)

  return (
    <SidePanel
      open={open}
      onClose={close}
      title={isEdit ? 'Projekt bearbeiten' : 'Neues Projekt'}
      footer={
        <>
          <Button variant="ghost" onClick={close} type="button">
            Abbrechen
          </Button>
          <Button
            onClick={onSubmit}
            type="button"
            disabled={
              !customerId ||
              !name.trim() ||
              name.length > NAME_MAX ||
              description.length > DESC_MAX ||
              dateRangeInvalid ||
              save.isPending
            }
          >
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        {save.isError && (
          <p role="alert" className="text-sm text-warning">
            {save.error?.message}
          </p>
        )}
        <div className="grid grid-cols-5 gap-3">
          <div className="col-span-3 space-y-2">
            <Label htmlFor="proj-name">Projektname</Label>
            <Input
              id="proj-name"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
              placeholder="z.B. Website-Relaunch"
              maxLength={NAME_MAX}
              autoFocus
            />
            <p className="text-[11px] text-text-tertiary tabular-nums text-right">
              {name.length} / {NAME_MAX}
            </p>
          </div>
          <div className="col-span-2 space-y-2">
            <Label htmlFor="proj-customer">Kunde</Label>
            <Select
              id="proj-customer"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
            >
              <option value="">— Kunde wählen —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="proj-start">Start (optional)</Label>
            <Input
              id="proj-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="proj-end">Ende (optional)</Label>
            <Input
              id="proj-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
            />
          </div>
        </div>
        {dateRangeInvalid && (
          <p className="text-xs text-danger">Ende darf nicht vor dem Start liegen.</p>
        )}
        <div className="space-y-2">
          <Label htmlFor="proj-desc">Beschreibung (optional)</Label>
          <textarea
            id="proj-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, DESC_MAX))}
            rows={4}
            maxLength={DESC_MAX}
            placeholder="Worum geht es in diesem Projekt? Ziele, Stakeholder, Besonderheiten…"
            className="flex w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3.5 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary transition-[border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 focus-visible:bg-bg-overlay hover:border-border-default/80 resize-y min-h-[96px]"
          />
          <p className="text-[11px] text-text-tertiary tabular-nums text-right">
            {description.length} / {DESC_MAX}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="proj-rate">Stundensatz (€)</Label>
            <Input
              id="proj-rate"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              type="number"
              min="0"
              step="0.5"
              placeholder="95"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="proj-budget">Budget (Std.)</Label>
            <Input
              id="proj-budget"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              type="number"
              min="0"
              step="0.5"
              placeholder="40"
            />
          </div>
        </div>

        {fibuFirmen.length > 0 && (
          <div className="space-y-2">
            <Label htmlFor="proj-fibu-firma">Firma in emberfibu (abweichend)</Label>
            <Select
              id="proj-fibu-firma"
              value={
                fibuFirmaId ?? (project && !fibuProjekt ? 'unloaded' : (fibuProjekt?.firmaId ?? ''))
              }
              onChange={(e) => setFibuFirmaId(e.target.value)}
            >
              {project && !fibuProjekt && (
                <option value="unloaded" disabled>
                  Zuordnung noch nicht geladen
                </option>
              )}
              <option value="">wie beim Kunden gemerkt</option>
              {fibuFirmen.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </Select>
            <p className="text-xs text-text-tertiary">
              {project && !fibuProjekt
                ? fibuLadefehler
                  ? 'Firmenzuordnung konnte nicht geladen werden. Ohne eigene Auswahl bleibt sie unverändert.'
                  : 'Firmenzuordnung wird geladen. Ohne eigene Auswahl bleibt sie unverändert.'
                : 'Nur setzen, wenn dieses Projekt über eine andere Firma abgerechnet wird als der Rest des Kunden.'}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="proj-color">Kalender-Farbe (optional)</Label>
          <div className="flex items-center gap-2">
            <input
              id="proj-color"
              type="color"
              value={color || '#6b6259'}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-14 rounded-lg border border-border-default cursor-pointer"
            />
            <Input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="leer = automatisch"
              className="flex-1"
            />
            {color && (
              <Button variant="ghost" size="sm" onClick={() => setColor('')} type="button">
                <Trash2 className="h-4 w-4" /> Zurücksetzen
              </Button>
            )}
          </div>
          <p className="text-xs text-text-tertiary">
            Wird für alle Zeiteinträge dieses Projekts im Kalender genutzt. Leer = automatische
            Farbwahl aus der Palette.
          </p>
        </div>

        {/* Linked documents — uses the existing documents vault as a pool */}
        <div className="space-y-2">
          <Label>Verlinkte Dokumente ({linkedDocIds.size})</Label>
          {documents.length === 0 ? (
            <p className="text-xs text-text-tertiary">
              Noch keine Dateien hochgeladen. Lege zuerst Dateien unter »Dateien« an, dann kannst du
              sie hier zum Projekt verknüpfen.
            </p>
          ) : (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-border-subtle bg-bg-overlay/30 divide-y divide-border-subtle">
              {documents.map((doc) => {
                const checked = linkedDocIds.has(doc.id)
                const otherProject = doc.projectId && doc.projectId !== project?.id
                return (
                  <label
                    key={doc.id}
                    className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-bg-overlay/40"
                  >
                    <input
                      type="checkbox"
                      disabled={!isAdmin || (!!project && !projDocs)}
                      checked={checked}
                      onChange={() => toggleDoc(doc.id)}
                      className="accent-accent h-4 w-4 shrink-0"
                    />
                    <span className="flex-1 min-w-0 truncate text-text-primary">
                      {doc.filename}
                    </span>
                    {otherProject && !checked && (
                      <span className="text-[11px] text-text-tertiary shrink-0">
                        anderes Projekt
                      </span>
                    )}
                  </label>
                )
              })}
            </div>
          )}
        </div>
      </form>
    </SidePanel>
  )
}
